<template>
  <div class="stripe-person-form-page">
    <div
      v-if="model"
      class="app-content-page app-content-page--transparent stripe-person-form-page__header"
    >
      <app-back-button
        v-if="enableBackButton"
        class="stripe-person-form-page__header__back"
        :i18n-code="routeBack.i18nCode"
        :route-name="routeBack.name"
        :route-params="routeBack.params"
      />

      <h1 class="app-content-title stripe-person-form-page__header__title">
        <app-i18n code="entities.stripePerson.form.title" />
      </h1>
    </div>

    <div class="app-content-page">
      <el-form
        v-if="model"
        :model="model"
        :rules="rules"
        class="form"
        ref="form"
        :validate-on-rule-change="false"
        v-loading="isLoading"
        @submit.native.prevent="doSubmit"
        @validate="onFieldValidation"
      >
        <el-steps
          v-if="!isEditing"
          :active="activeStepIndex"
          align-center
          finish-status="success"
        >
          <el-step :title="i18n('entities.stripePerson.form.subtitle.information')" />
          <el-step :title="i18n('entities.stripePerson.form.subtitle.address')" />
          <el-step :title="i18n('entities.stripePerson.form.subtitle.verification')" />
        </el-steps>

        <!-- Step 1 -->
        <template v-if="isEditing || activeStepIndex === 0">
          <h2 class="app-content-subtitle">
            <app-i18n code="entities.stripePerson.form.subtitle.information" />
          </h2>

          <el-row :gutter="16">
            <el-col :lg="12" :md="12" :sm="24">
              <el-form-item
                :label="fields.firstName.label"
                :prop="fields.firstName.name"
                :error="getFieldError(fields.firstName.name).message"
                required
              >
                <el-input
                  v-model="model[fields.firstName.name]"
                  :placeholder="i18n(`entities.stripePerson.placeholders.${fields.firstName.name}`)"
                  prefix-icon="icon-player"
                  :disabled="isEditing"
                />
              </el-form-item>
            </el-col>

            <el-col :lg="12" :md="12" :sm="24">
              <el-form-item
                :label="fields.lastName.label"
                :prop="fields.lastName.name"
                :error="getFieldError(fields.lastName.name).message"
                required
              >
                <el-input
                  v-model="model[fields.lastName.name]"
                  prefix-icon="icon-player"
                  :placeholder="i18n(`entities.stripePerson.placeholders.${fields.lastName.name}`)"
                  :disabled="isEditing"
                />
              </el-form-item>
            </el-col>

            <el-col :lg="9" :md="12" :sm="24">
              <el-form-item
                :label="fields.phone.label"
                :prop="fields.phone.name"
                :required="fields.phone.required"
                :error="getFieldError(fields.phone.name).message"
              >
                <phone-input v-model="model[fields.phone.name]" />
              </el-form-item>
            </el-col>

            <el-col :lg="15" :md="12" :sm="24">
              <el-form-item
                :label="fields.birthdate.label"
                :prop="fields.birthdate.name"
                :error="getFieldError(fields.birthdate.name).message"
                required
              >
                <el-date-picker
                  type="date"
                  :placeholder="i18n(`entities.stripePerson.placeholders.${fields.birthdate.name}`)"
                  v-model="model[fields.birthdate.name]"
                  :disabled="isEditing"
                />
              </el-form-item>
            </el-col>

            <el-col :lg="9" :md="12" :sm="24">
              <el-form-item
                :label="fields.jobTitle.label"
                :prop="fields.jobTitle.name"
                :error="getFieldError(fields.jobTitle.name).message"
                required
              >
                <el-input
                  v-model="model[fields.jobTitle.name]"
                  :placeholder="i18n(`entities.stripePerson.placeholders.${fields.jobTitle.name}`)"
                  prefix-icon="icon-player"
                />
              </el-form-item>
            </el-col>

            <el-col :lg="15" :md="12" :sm="24">
              <el-form-item
                :label="fields.email.label"
                :prop="fields.email.name"
                :required="fields.email.required"
                :error="getFieldError(fields.email.name).message"
              >
                <el-input
                  v-model="model[fields.email.name]"
                  :placeholder="i18n(`entities.stripePerson.placeholders.${fields.email.name}`)"
                  prefix-icon="icon-mail"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <h3 class="app-content-heading">
            <app-i18n code="entities.stripePerson.fields.role" />
          </h3>

          <el-row :gutter="16">
            <el-col :lg="24" :md="24" :sm="24">
              <el-form-item
                class="stripe-person-form-page__form-item--checkbox"
                :prop="fields.isAccountOpener.name"
                :error="getFieldError(fields.isAccountOpener.name).message"
              >
                <el-checkbox
                  v-model="model[fields.isAccountOpener.name]"
                  disabled
                >
                  <app-i18n code="entities.stripePerson.fields.isAccountOpener" />
                  <el-tooltip
                    effect="dark"
                    :content="i18n('entities.stripePerson.tooltips.accountOpener')"
                    placement="right"
                    popper-class="stripe-person-form-page__tooltip"
                  >
                    <i class="icon-info" />
                  </el-tooltip>
                </el-checkbox>
              </el-form-item>
              <el-form-item
                class="stripe-person-form-page__form-item--checkbox"
                :prop="fields.isDirector.name"
                :error="getFieldError(fields.isDirector.name).message"
              >
                <el-checkbox v-model="model[fields.isDirector.name]">
                  <app-i18n code="entities.stripePerson.fields.isDirector" />
                  <el-tooltip
                    effect="dark"
                    :content="i18n('entities.stripePerson.tooltips.director')"
                    placement="right"
                    popper-class="stripe-person-form-page__tooltip"
                  >
                    <i class="icon-info" />
                  </el-tooltip>
                </el-checkbox>
              </el-form-item>
              <el-form-item
                class="stripe-person-form-page__form-item--checkbox"
                :prop="fields.isExecutive.name"
                :error="getFieldError(fields.isExecutive.name).message"
                :rules="getFieldRules(fields.isExecutive.name)"
                ref="isExecutiveField"
              >
                <el-checkbox
                  v-model="model[fields.isExecutive.name]"
                  @change="resetAccountOpenerValidation"
                >
                  <app-i18n code="entities.stripePerson.fields.isExecutive" />
                  <el-tooltip
                    effect="dark"
                    :content="i18n('entities.stripePerson.tooltips.executive')"
                    placement="right"
                    popper-class="stripe-person-form-page__tooltip"
                  >
                    <i class="icon-info" />
                  </el-tooltip>
                </el-checkbox>
              </el-form-item>
              <el-form-item
                class="stripe-person-form-page__form-item--checkbox"
                :prop="fields.isOwner.name"
                :error="getFieldError(fields.isOwner.name).message"
                :rules="getFieldRules(fields.isOwner.name)"
                ref="isOwnerField"
              >
                <el-checkbox
                  v-model="model[fields.isOwner.name]"
                  @change="resetAccountOpenerValidation"
                >
                  <app-i18n code="entities.stripePerson.fields.isOwner" />
                  <el-tooltip
                    effect="dark"
                    :content="i18n('entities.stripePerson.tooltips.owner')"
                    placement="right"
                    popper-class="stripe-person-form-page__tooltip"
                  >
                    <i class="icon-info" />
                  </el-tooltip>
                </el-checkbox>
              </el-form-item>
            </el-col>

            <el-col
              v-if="!!model[fields.isOwner.name]"
              :lg="6" :md="6" :sm="24"
            >
              <el-form-item
                class="stripe-person-form-page__form-item--ownership"
                :label="fields.ownershipPercentage.label"
                :prop="fields.ownershipPercentage.name"
                :error="getFieldError(fields.ownershipPercentage.name).message"
                :required="!!model[fields.isOwner.name]"
              >
                <el-input
                  prefix-icon="icon-percent"
                  :placeholder="i18n(`entities.stripePerson.placeholders.${fields.ownershipPercentage.name}`)"
                  type="number"
                  v-model.number="model[fields.ownershipPercentage.name]"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </template>

        <!-- Step 2 -->
        <template v-if="isEditing || activeStepIndex === 1">
          <h2 class="app-content-subtitle">
            <app-i18n code="entities.stripePerson.form.subtitle.address" />
          </h2>

          <app-address-form-partial
            ref="addressForm"
            field-name="address"
            :address.sync="model[fields.address.name]"
            :errors="getFieldError(fields.address.name)"
            @validate="onFieldValidation"
          />
        </template>

        <!-- Step 3 -->
        <template v-if="isEditing || activeStepIndex === 2">
          <h2 class="app-content-subtitle">
            <app-i18n code="entities.stripePerson.form.subtitle.verification" />
          </h2>

          <el-row
            v-if="isIDNumberNeeded"
            :gutter="16"
          >
            <el-col :lg="6" :md="12" :sm="24">
              <app-field-provided
                v-if="isProvidedConfig(fields.ssnLast4.name).provided"
                :label="fields.ssnLast4.label"
              />
              <el-form-item
                v-else
                class="stripe-person-form-page__form-item--ssn"
                :label="fields.ssnLast4.label"
                :prop="fields.ssnLast4.name"
                :error="getFieldError(fields.ssnLast4.name).message"
                :required="isEditing || activeStepIndex === 2"
              >
                <el-input
                  v-model="model[fields.ssnLast4.name]"
                  :placeholder="i18n(`entities.stripePerson.placeholders.${fields.ssnLast4.name}`)"
                  prefix-icon="icon-lock"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="16">
            <el-col :lg="8" :md="12" :sm="24">
              <app-field-provided
                v-if="isProvidedConfig(fields.verificationIdFront.name).provided"
                :unverified="isProvidedConfig(fields.verificationIdFront.name).status === 'unverified'"
                :label="fields.verificationIdFront.label"
                @replace="replaceProvided = { ...replaceProvided, [fields.verificationIdFront.name]: true }"
              />
              <el-form-item
                v-else
                :prop="fields.verificationIdFront.name"
                :error="getFieldError(fields.verificationIdFront.name).message"
                :required="isEditing || (fields.verificationIdFront.required && activeStepIndex === 2)"
              >
                <app-image-upload
                  v-model="model[fields.verificationIdFront.name]"
                  :schema="fields.verificationIdFront.fileSchema"
                  :placeholder="i18n(`entities.stripePerson.placeholders.${fields.verificationIdFront.name}`)"
                  :max="fields.verificationIdFront.max"
                  :tooltip-content-i18n-code="idUploadTooltipI18nKey"
                  tooltip-icon-class="icon-info"
                  wide
                  stripe-upload
                  @success="() => $refs.form.validateField(fields.verificationIdFront.name)"
                />
              </el-form-item>
            </el-col>

            <el-col :lg="8" :md="12" :sm="24">
              <app-field-provided
                v-if="isProvidedConfig(fields.verificationIdBack.name).provided"
                :unverified="isProvidedConfig(fields.verificationIdBack.name).status === 'unverified'"
                :label="fields.verificationIdBack.label"
                @replace="replaceProvided = { ...replaceProvided, [fields.verificationIdBack.name]: true }"
              />
              <el-form-item
                v-else
                :prop="fields.verificationIdBack.name"
                :error="getFieldError(fields.verificationIdBack.name).message"
                :required="isEditing || (fields.verificationIdBack.required && activeStepIndex === 2)"
              >
                <app-image-upload
                  v-model="model[fields.verificationIdBack.name]"
                  :schema="fields.verificationIdBack.fileSchema"
                  :placeholder="i18n(`entities.stripePerson.placeholders.${fields.verificationIdBack.name}`)"
                  :max="fields.verificationIdBack.max"
                  :tooltip-content-i18n-code="idUploadTooltipI18nKey"
                  tooltip-icon-class="icon-info"
                  wide
                  stripe-upload
                  @success="() => $refs.form.validateField(fields.verificationIdBack.name)"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <template v-if="personNeedsAdditionalDocument">
            <h2 class="app-content-subtitle">
              <app-i18n code="entities.stripePerson.form.subtitle.additionalDocument" />
            </h2>

            <el-row :gutter="16">
              <el-col :lg="8" :md="12" :sm="24">
                <app-field-provided
                  v-if="isProvidedConfig(fields.additionalDocumentFront.name).provided"
                  :unverified="isProvidedConfig(fields.additionalDocumentFront.name).status === 'unverified'"
                  :label="fields.additionalDocumentFront.label"
                  @replace="replaceProvided = { ...replaceProvided, [fields.additionalDocumentFront.name]: true }"
                />
                <el-form-item
                  v-else
                  :prop="fields.additionalDocumentFront.name"
                  :error="getFieldError(fields.additionalDocumentFront.name).message"
                  :required="isEditing || (fields.additionalDocumentFront.required && activeStepIndex === 2)"
                >
                  <app-image-upload
                    v-model="model[fields.additionalDocumentFront.name]"
                    :schema="fields.additionalDocumentFront.fileSchema"
                    :placeholder="i18n(`entities.stripePerson.placeholders.${fields.additionalDocumentFront.name}`)"
                    :max="fields.additionalDocumentFront.max"
                    tooltip-content-i18n-code="entities.stripePerson.tooltips.additionalDocument.default"
                    tooltip-icon-class="icon-info"
                    wide
                    stripe-upload
                    @success="() => $refs.form.validateField(fields.additionalDocumentFront.name)"
                  />
                </el-form-item>
              </el-col>

              <el-col :lg="8" :md="12" :sm="24">
                <app-field-provided
                  v-if="isProvidedConfig(fields.additionalDocumentBack.name).provided"
                  :unverified="isProvidedConfig(fields.additionalDocumentBack.name).status === 'unverified'"
                  :label="fields.additionalDocumentBack.label"
                  @replace="replaceProvided = { ...replaceProvided, [fields.additionalDocumentBack.name]: true }"
                />
                <el-form-item
                  v-else
                  :prop="fields.additionalDocumentBack.name"
                  :error="getFieldError(fields.additionalDocumentBack.name).message"
                  :required="isEditing || (fields.additionalDocumentBack.required && activeStepIndex === 2)"
                >
                  <app-image-upload
                    v-model="model[fields.additionalDocumentBack.name]"
                    :schema="fields.additionalDocumentBack.fileSchema"
                    :placeholder="i18n(`entities.stripePerson.placeholders.${fields.additionalDocumentBack.name}`)"
                    :max="fields.additionalDocumentBack.max"
                    tooltip-content-i18n-code="entities.stripePerson.tooltips.additionalDocument.default"
                    tooltip-icon-class="icon-info"
                    wide
                    stripe-upload
                    @success="() => $refs.form.validateField(fields.additionalDocumentBack.name)"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </template>
        </template>

        <!-- Global error message -->
        <el-form-item
          v-if="globalErrorMessage"
          :error="globalErrorMessage"
        />

        <!-- Form controls -->
        <el-form-item>
          <div class="form-buttons form-buttons--sides">
            <!-- Back button -->
            <el-button
              :disabled="saveLoading"
              @click="doBack"
              type="secondary"
            >
              <app-i18n code="common.backButton" />
            </el-button>

            <!-- Create person button -->
            <el-button
              v-if="isEditing || activeStepIndex === 2"
              :disabled="saveLoading"
              @click="doSubmit"
              type="primary"
            >
              <app-i18n
                v-if="isEditing"
                code="entities.stripePerson.update.title"
              />
              <app-i18n
                v-else
                code="entities.stripePerson.create.title"
              />
            </el-button>

            <!-- Continue button -->
            <el-button
              v-else
              :disabled="saveLoading"
              @click="doContinue"
              type="primary"
            >
              <app-i18n code="common.continue" />
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isString } from 'lodash';

import StripeFrontend from 'shared-stripe-services/dist/Frontend';

import config from '@/config';

import { i18n, i18nExists } from '@shared/i18n';
import { FormSchema } from '@shared/form/form-schema';
import { mapStripeFieldErrors, getStripeFieldNames } from '@shared/fields/helpers/mapStripeFields';

import { StripePersonModel } from '@modules/stripe/stripe-person/stripe-person-model';

const stripeFrontend = new StripeFrontend({
  publishableKey: config.stripeConfig.publishableKey,
  fileUploadEndpoint: config.stripeConfig.fileUploadEndpoint,
});

export default {
  name: 'app-stripe-person-form-page',
  props: {
    id: String,
    connectedAccountId: String,
    isOwner: Boolean,
    isDirector: Boolean,
    isExecutive: Boolean,
  },
  data() {
    return {
      model: {},
      tokenLoading: false,
      activeStepIndex: 0,
      i18n,
      prevRoute: null,
      replaceProvided: {},
    };
  },
  computed: {
    ...mapGetters({
      record: 'stripePerson/form/record',
      fieldErrors: 'stripePerson/form/fieldErrors',
      globalErrorMessage: 'stripePerson/form/globalErrorMessage',
      newLoading: 'stripePerson/form/newLoading',
      findLoading: 'stripePerson/form/findLoading',
      saveLoading: 'stripePerson/form/saveLoading',
      hasAccountOpener: 'stripePerson/form/connectedAccountHasOpener',
      currentOwnershipPercentage: 'stripePerson/form/connectedAccountCurrentOwnershipPercentage',
      currentManage: 'auth/manage/currentManage',
      isRootScreen: 'layout/isRootScreen',
    }),

    isLoading() {
      return this.findLoading || this.newLoading || this.saveLoading || this.tokenLoading;
    },

    isEditing() {
      return !!this.id;
    },

    formSchema() {
      return new FormSchema([
        this.fields.id,
        this.fields.firstName,
        this.fields.lastName,
        this.fields.phone,
        this.fields.birthdate,
        this.fields.jobTitle,
        this.fields.email,
        this.fields.isAccountOpener,
        this.fields.isDirector,
        this.fields.isExecutive,
        this.fields.isOwner,
        this.fields.ownershipPercentage,
        this.fields.address,
        this.fields.ssnLast4,
        this.fields.ssnLast4Provided,
        this.fields.verificationIdFront,
        this.fields.verificationIdBack,
        this.fields.additionalDocumentFront,
        this.fields.additionalDocumentBack,
      ]);
    },

    rules() {
      const rules = this.formSchema.rules();
      if (!this.isEditing && this.activeStepIndex !== 2) {
        delete rules.ssnLast4;
        delete rules.verificationIdFront;
        delete rules.verificationIdBack;
        delete rules.additionalDocumentFront;
        delete rules.additionalDocumentBack;
      }
      return rules;
    },

    fields() {
      const { countryCode, currentOwnershipPercentage } = this;
      const maxOwnershipPercentage = (100 - currentOwnershipPercentage);
      return StripePersonModel.fields({ countryCode, maxOwnershipPercentage });
    },

    stripeFieldsMap() {
      return {
        'email': {
          fields: [this.fields.email.name],
          stepIndex: 0,
        },
        'person[phone]': {
          fields: [this.fields.phone.name],
          stepIndex: 0,
        },
        'person[dob][year]': {
          fields: [this.fields.birthdate.name],
          stepIndex: 0,
          message: i18n('entities.stripePerson.validation.minAge'),
        },
        'relationship[representative]': {
          fields: [this.fields.isAccountOpener.name],
          stepIndex: 0,
          message: i18n('entities.stripePerson.validation.accountOpener'),
        },
        'relationship[percent_ownership]': {
          fields: [this.fields.ownershipPercentage.name],
          stepIndex: 0,
          message: i18n('entities.stripePerson.validation.maxOwnershipPercentage'),
        },
      };
    },

    mappedFieldErrors() {
      try {
        const { isLoading, fieldErrors, stripeFieldsMap } = this;
        if (isLoading) return {};
        return mapStripeFieldErrors({
          fieldErrors,
          stripeFieldsMap,
          modelName: 'StripePerson',
          model: this.model,
        });
      } catch (e) {
        const message = i18n('common.unknownError');
        this.doSetStripeError({ message });
        return {};
      }
    },

    subpropertyName({ currentManage }) {
      return `${currentManage.property.name} - ${currentManage.subproperty.name}`;
    },

    countryCode({ currentManage }) {
      const defaultCountry = currentManage.subproperty.country

      if (!this.model) return defaultCountry
      if (!this.model.address) return defaultCountry
      if (!this.model.address.country) return defaultCountry

      return this.model.address.country
    },

    idUploadTooltipI18nKey() {
      const defaultI18nKey = 'entities.stripePerson.tooltips.verificationId.default';
      const countryI18nKey = `entities.stripePerson.tooltips.verificationId.${this.countryCode}`;

      if (i18nExists(countryI18nKey)) return i18n(countryI18nKey);
      return i18n(defaultI18nKey);
    },

    isIDNumberNeeded({ currentManage }) {
      return currentManage.subproperty.country !== 'ES'
    },

    enableBackButton() {
      return isString(this.routeBack.i18nCode) && this.routeBack.name;
    },

    routeBack() {
     if (this.prevRoute && this.prevRoute.name === 'finantialSettings') {
        return  {
          name: 'finantialSettings',
          params: {
            propertyId: this.currentManage.property.id,
            subpropertyId: this.currentManage.subproperty.id,
          },
          i18nCode: 'subpropertySettings.menu',
        }
      } else {
        return  {
          name: 'home',
          params: null,
          i18nCode: 'home.nav.dashboard',
        }
      }
    },

    getFieldRules() {
      const { model, fields } = this
      const isAccountOpener = !!model[fields.isAccountOpener.name]
      const isExecutive = !!model[fields.isExecutive.name]
      const isOwner = !!model[fields.isOwner.name]

      const validationMessage = i18n('entities.stripePerson.validation.accountOpenerExecutiveOrOwner');

      const validator = (rule, value, callback) => {
        try {
          if (!value) throw validationMessage;
          callback();
        } catch (msg) {
          callback(msg);
        }
      };

      return fieldName => {
        switch (fieldName) {
          case 'isExecutive':
            return isAccountOpener && !isOwner ? [{ validator }] : []
          case 'isOwner':
            return isAccountOpener && !isExecutive ? [{ validator }] : []
          default:
            return []
        }
      }
    },

    personNeedsAdditionalDocument() {
      const { requirements, verification } = this.record
      const additionalDocument = verification && verification.additionalDocument

      if (additionalDocument && (additionalDocument.front || additionalDocument.back)) return true

      if (!requirements) return false

      const dueRequirements = [...(new Set([
        ...(requirements.pastDue || []),
        ...(requirements.currentlyDue || []),
        ...(requirements.eventuallyDue || []),
      ]))]

      return dueRequirements.includes('verification.additional_document')
    },
  },

  mounted() {
    this.initialize();
  },

  beforeRouteEnter(to, from, next) {
    next(vm => vm.prevRoute = from)
  },

  methods: {
    ...mapActions({
      doFind: 'stripePerson/form/doFind',
      doNew: 'stripePerson/form/doNew',
      doUpdate: 'stripePerson/form/doUpdate',
      doCreate: 'stripePerson/form/doCreate',
      doHandleStripeError: 'stripePerson/form/doHandleStripeError',
      doSetStripeError: 'stripePerson/form/doSetStripeError',
      doClearStripeError: 'stripePerson/form/doClearStripeError',
    }),

    async initialize() {
      if (this.isEditing) {
        await this.doFind({
          id: this.id,
          propertyId: this.currentManage.property.id,
          connectedAccountId: this.$route.params.connectedAccountId,
        });
      } else {
        await this.doNew({
          connectedAccountId: this.$route.params.connectedAccountId,
          propertyId: this.currentManage.property.id,
        });
      }

      this.model = this.formSchema.initialValues({
        ...this.record,
        ...(this.isOwner && { isOwner: true }),
        ...(this.isDirector && { isDirector: true }),
        ...(this.isExecutive && { isExecutive: true }),
      });

      if (!this.isEditing) return;

      try {
        await this.$nextTick();
        await this.doValidation();
      } catch (e) {
        // 
      }
    },

    presenter(row, fieldName) {
      const { countryCode } = this;
      return StripePersonModel.presenter(
        row,
        fieldName,
        countryCode,
      );
    },

    doBack() {
      if (this.activeStepIndex === 0) {
        const { name, params } = this.routeBack
        const route = { name }
        if (params) route.params = params
        this.$router.push(route)
      } else {
        this.activeStepIndex--;
      }
    },

    async doContinue() {
      try {
        await this.doValidation();
        this.activeStepIndex++;
      } catch (error) {
        return;
      }
    },

    async doValidation() {
      try {
        const { form, addressForm } = this.$refs;
        const forms = [];

        if (form) forms.push(form);
        if (addressForm) forms.push(addressForm.$refs.form);

        await Promise.all(forms.map(form => {
          form.clearValidate();
          return form.validate();
        }));
      } catch (error) {
        throw error;
      }
    },

    async doReset() {
      this.model = this.formSchema.initialValues(this.record);

      await this.$nextTick();
      this.$refs.form.clearValidate();
      
      if (this.$refs.addressForm) {
        this.$refs.addressForm.doReset();
        await this.$nextTick();
        this.$refs.addressForm.$refs.form.clearValidate();
      }
    },

    async doSubmit() {
      try {
        await this.doValidation();
      } catch (error) {
        return;
      }

      try {
        this.tokenLoading = true;

        const { id, ...data } = this.formSchema.cast(this.model);

        const tokenResult = await this.generateStripePersonToken(data);
        if ('error' in tokenResult) throw tokenResult.error;

        const { token: { id: personToken } } = tokenResult;

        const values = {
          personToken,
          phone: data.phone,
        };

        const backToSettings = this.prevRoute && this.prevRoute.name === 'finantialSettings'

        if (this.isEditing) {
          return this.doUpdate({
            id,
            ...values,
            propertyId: this.currentManage.property.id,
            subpropertyId: this.currentManage.subproperty.id,
            stripePersonId: this.record.stripePersonId,
            backToSettings,
          });
        } else {
          return this.doCreate({
            ...values,
            propertyId: this.currentManage.property.id,
            subpropertyId: this.currentManage.subproperty.id,
            backToSettings,
          });
        }
      } catch (error) {
        this.doHandleStripeError(error);
      } finally {
        this.tokenLoading = false;
      }
    },

    async generateStripePersonToken(data) {
      const percentOwnership = data.isOwner ? data.ownershipPercentage : null

      const verificationDocumentFrontToken = !!data.verificationIdFront && data.verificationIdFront.length
        ? data.verificationIdFront[0].data.id
        : null

      const verificationDocumentBackToken = !!data.verificationIdBack && data.verificationIdBack.length
        ? data.verificationIdBack[0].data.id
        : null

      const additionalDocumentFrontToken = !!data.additionalDocumentFront && data.additionalDocumentFront.length
        ? data.additionalDocumentFront[0].data.id
        : null

      const additionalDocumentBackToken = !!data.additionalDocumentBack && data.additionalDocumentBack.length
        ? data.additionalDocumentBack[0].data.id
        : null

      return stripeFrontend.createPersonToken({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: `${data.phone.prefix}${data.phone.number}`,
        birthDateDay: data.birthdate.day,
        birthDateMonth: data.birthdate.month,
        birthDateYear: data.birthdate.year,
        ssnLast4: data.ssnLast4,
        addressLine1: data.address.line1,
        addressLine2: data.address.line2,
        city: data.address.city,
        state: data.address.state,
        postalCode: data.address.postalCode,
        country: data.address.country,
        companyTitle: data.jobTitle,
        isAccountOpener: data.isAccountOpener,
        isExecutive: data.isExecutive,
        isDirector: data.isDirector,
        isOwner: data.isOwner,
        percentOwnership,
        verificationDocumentFrontToken,
        verificationDocumentBackToken,
        additionalDocumentFrontToken,
        additionalDocumentBackToken,
      });
    },

    isProvidedConfig(fieldName) {
      if (!this.isEditing) return { provided: false }
      if (!this.record) return { provided: false }
      if (!this.record.verification) return { provided: false }

      switch (fieldName) {
        case 'ssnLast4':
          return {
            provided: this.record.ssnLast4Provided,
          }
        case 'verificationIdFront':
          return {
            provided: !this.replaceProvided[fieldName] && !!this.record.verification.document.front,
            status: this.record.verification.status,
          }
        case 'verificationIdBack':
          return {
            provided: !this.replaceProvided[fieldName] && !!this.record.verification.document.back,
            status: this.record.verification.status,
          }
        case 'additionalDocumentFront':
          return {
            provided: !this.replaceProvided[fieldName] && !!this.record.verification.additionalDocument && !!this.record.verification.additionalDocument.front,
            status: this.record.verification.status,
          }
        case 'additionalDocumentBack':
          return {
            provided: !this.replaceProvided[fieldName] && !!this.record.verification.additionalDocument && !!this.record.verification.additionalDocument.back,
            status: this.record.verification.status,
          }
      }
    },

    getFieldError(fieldName) {
      const mappedError = this.mappedFieldErrors[fieldName];
      if (!mappedError) return {};
      return mappedError;
    },

    async resetAccountOpenerValidation() {
      try {
        await this.$nextTick()
        this.$refs.isExecutiveField.clearValidate()
        this.$refs.isOwnerField.clearValidate()
      } catch (e) {
        // 
      }
    },

    findKeyRecursively(object, key) {
      let value;

      Object.keys(object).some(k => {
        if (k === key) {
          value = object[k];
          return true;
        }
        if (object[k] && typeof object[k] === 'object') {
          value = this.findKeyRecursively(object[k], key);
          return value !== undefined;
        }
      });

      return value;
    },

    onFieldValidation(formFieldName) {
      if (!this.globalErrorMessage) return;

      const { stripeFieldsMap } = this;
      const fieldNames = getStripeFieldNames({
        fieldName: formFieldName,
        stripeFieldsMap,
      });

      if (!fieldNames || !fieldNames.length) return;

      this.doClearStripeError({ fieldNames });
    },
  },
  watch: {
    mappedFieldErrors: {
      handler(val) {
        if (!Object.keys(val).length) return;
        this.activeStepIndex = this.findKeyRecursively(val, 'stepIndex');
      },
      deep: true
    },
  }
};
</script>

<style lang="scss">
.stripe-person-form-page {
  &__header {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: 16px auto;
    grid-template-areas:
      'back .'
      'title radio';
    grid-row-gap: 24px;
    align-items: center;
    margin-left: 8px;

    &__back {
      grid-area: back;
    }

    &__radio {
      grid-area: radio;
    }

    &__title {
      grid-area: title;
      margin: 0;
    }
  }

  &__form-item {
    body .el-form &--ssn,
    body .el-form &--ownership {
      .el-form-item__error {
        white-space: nowrap;
      }
    }

    body .el-form &--checkbox {
      margin-bottom: 8px;

      .el-checkbox__label {
        display: inline-flex;
        align-items: center;
        margin-left: 8px;
        font-size: 14px;
        font-weight: normal;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.29;
        letter-spacing: -0.3px;
        color: #333333;
      }

      .el-checkbox__inner {
        width: 20px;
        height: 20px;

        &::after {
          width: 4px;
          height: 12px;

          left: 6px;
          top: 0;
        }
      }
    }
  }

  .el-tooltip {
    margin-left: 16px;

    &::before {
      font-size: 20px;
      color: #cccccc;
    }
  }

  &__message {
    margin: 0;
    color: #404040;
  }

  &__tooltip {
    max-width: 240px;
    padding: 8px 8px 8px 16px;
  }

  .form-buttons {
    margin-top: 24px;
  }
}
</style>
